(() => {
  'use strict';

  const cleanText = (value, limit = 2000) => String(value || '').replace(/\s+/g, ' ').trim().slice(0, limit);

  function readReference(html, handle, url) {
    const page = new DOMParser().parseFromString(html, 'text/html');
    const reference = page.querySelector('[data-kn-product-reference]');
    if (!reference || reference.dataset.productHandle !== handle || !reference.dataset.productTitle) {
      throw new Error('Product reference unavailable');
    }
    const details = Array.from(page.querySelectorAll('.kosmo-product-accordion__item')).slice(0, 6).map(item => ({
      label: cleanText(item.querySelector('summary')?.textContent, 100),
      value: cleanText(item.querySelector('.kosmo-product-accordion__content')?.textContent)
    })).filter(detail => detail.label && detail.value);
    return {
      title: cleanText(reference.dataset.productTitle, 300),
      type: cleanText(reference.dataset.productType, 200),
      image: reference.dataset.productImage || '',
      url,
      details
    };
  }

  function briefFor(reference, copy) {
    const referenceLines = [
      `${copy.reference_label}: ${reference.title}`,
      reference.type ? `${copy.type_label}: ${reference.type}` : '',
      `${copy.link_label}: ${reference.url}`
    ].filter(Boolean).join('\n');
    return [copy.intro, referenceLines].join('\n\n');
  }

  async function initialize(root) {
    if (root.dataset.knConsultationInitialized) return;
    root.dataset.knConsultationInitialized = 'true';
    const success = root.querySelector('[data-kn-contact-submitted]');
    const leadStorageKey = 'kn_contact_lead_recorded';
    const pendingStorageKey = 'kn_contact_pending';
    root.querySelector('form')?.addEventListener('submit', () => {
      const pending = {at: Date.now(), id: typeof crypto.randomUUID === 'function' ? crypto.randomUUID() : Date.now().toString(36) + '-' + Math.random().toString(36).slice(2), method: 'contact_form', attachment_count: 0};
      try { sessionStorage.removeItem(leadStorageKey); sessionStorage.setItem(pendingStorageKey, JSON.stringify(pending)); } catch (error) {}
      try { if (window.Shopify?.analytics?.publish) Promise.resolve(window.Shopify.analytics.publish('kn:contact_submit_attempt', {form_id: 'ContactForm', method: pending.method, submission_id: pending.id, status: 'attempt'})).catch(() => {}); } catch (error) {}
    });
    if (success) {
      root.querySelector('.form-status')?.focus();
      let recorded = false;
      let pending = null;
      try { pending = JSON.parse(sessionStorage.getItem(pendingStorageKey) || 'null'); if (pending && Date.now() - pending.at > 7200000) pending = null; } catch (error) {}
      try {
        const intake = JSON.parse(sessionStorage.getItem('kn_intake_pending') || 'null');
        if (intake && Date.now() - intake.at < 7200000 && typeof intake.session_id === 'string' && typeof intake.upload_token === 'string') {
          fetch('https://requests.kosmonext.com/api/intake/sessions/' + encodeURIComponent(intake.session_id) + '/confirm', {method: 'POST', credentials: 'omit', headers: {Authorization: 'Bearer ' + intake.upload_token, 'Content-Type': 'application/json'}, body: '{}', keepalive: true}).then(response => { if (response.ok) sessionStorage.removeItem('kn_intake_pending'); }).catch(() => {});
        }
      } catch (error) {}
      try { recorded = sessionStorage.getItem(leadStorageKey) === '1'; } catch (error) {}
      if (!recorded) {
        let attempts = 0;
        const publishLead = () => {
          const analytics = window.Shopify && window.Shopify.analytics;
          if (!analytics || typeof analytics.publish !== 'function') {
            if (++attempts < 40) window.setTimeout(publishLead, 250);
            return;
          }
          try {
            Promise.resolve(analytics.publish('kn:contact_submitted', {form_id: 'ContactForm', method: pending?.method || 'contact_form', submission_id: pending?.id || '', attachment_count: pending?.attachment_count || 0, confirmed: true, status: 'success', transport: 'shopify_contact'})).then(() => {
              try { sessionStorage.setItem(leadStorageKey, '1'); sessionStorage.removeItem(pendingStorageKey); if (pending?.method === 'oem_development_brief') sessionStorage.removeItem('kn-oem-submission-v1'); } catch (error) {}
            }).catch(() => {});
          } catch (error) {}
        };
        publishLead();
      }
      return;
    }
    const handle = new URLSearchParams(window.location.search).get('product');
    if (!handle || !/^[a-z0-9][a-z0-9-]{0,254}$/.test(handle)) return;

    const card = root.querySelector('[data-kn-reference-card]');
    const status = root.querySelector('[data-kn-reference-status]');
    const content = root.querySelector('[data-kn-reference-content]');
    const body = root.querySelector('#ContactForm-body');
    const form = root.querySelector('form');
    const copy = JSON.parse(root.querySelector('[data-kn-consultation-copy]').textContent);
    const base = new URL(root.dataset.productRoot, window.location.origin);
    if (base.origin !== window.location.origin) return;
    const productUrl = new URL(`products/${encodeURIComponent(handle)}`, base).href;
    let bodyEdited = false;
    body.addEventListener('input', () => { bodyEdited = true; });
    const fallbackLink = root.querySelector('[data-kn-reference-field="link"]');
    fallbackLink.value = productUrl; fallbackLink.disabled = false;
    const fallbackName = root.querySelector('[data-kn-reference-field="reference"]');
    fallbackName.value = handle; fallbackName.disabled = false;
    if (!body.value.trim()) body.value = copy.intro + '\n\n' + copy.link_label + ': ' + productUrl;
    const whatsapp = root.querySelector('[data-kn-contact-whatsapp]');
    if (whatsapp) whatsapp.href = 'https://wa.me/821071608562?text=' + encodeURIComponent(body.value);
    card.hidden = false;
    card.setAttribute('aria-busy', 'true');
    status.textContent = copy.loading;
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 8000);

    try {
      const response = await fetch(productUrl, { credentials: 'same-origin', signal: controller.signal });
      if (!response.ok) throw new Error('Product reference unavailable');
      const reference = readReference(await response.text(), handle, productUrl);
      if (!root.isConnected) return;
      root.querySelector('[data-kn-reference-name]').textContent = reference.title;
      const type = root.querySelector('[data-kn-reference-type]');
      type.textContent = reference.type;
      type.hidden = !reference.type;
      root.querySelector('[data-kn-reference-link]').href = reference.url;
      const image = root.querySelector('[data-kn-reference-image]');
      if (reference.image) {
        const imageUrl = new URL(reference.image, window.location.origin);
        if (imageUrl.protocol === 'https:') {
          image.src = imageUrl.href;
          image.alt = reference.title;
          image.hidden = false;
        }
      }
      const details = root.querySelector('[data-kn-reference-details]');
      if (reference.details.length) {
        const list = root.querySelector('[data-kn-reference-list]');
        reference.details.forEach(detail => {
          const label = document.createElement('dt');
          const value = document.createElement('dd');
          label.textContent = detail.label;
          value.textContent = detail.value;
          list.append(label, value);
        });
        details.hidden = false;
      }
      const values = {
        reference: reference.title,
        type: reference.type,
        link: reference.url,
        details: reference.details.map(detail => `${detail.label}: ${detail.value}`).join('\n')
      };
      Object.entries(values).forEach(([key, value]) => {
        const field = root.querySelector(`[data-kn-reference-field="${key}"]`);
        field.value = value;
        field.disabled = !value;
      });
      if (!bodyEdited) body.value = briefFor(reference, copy);
      const heading = root.querySelector('[data-kn-contact-heading]');
      if (heading) heading.textContent = copy.heading;
      const button = form.querySelector('button[type="submit"]');
      button.dataset.buttonText = copy.send;
      button.querySelector('[data-kn-submit-label]').textContent = copy.send;
      if (whatsapp) whatsapp.href = 'https://wa.me/821071608562?text=' + encodeURIComponent(briefFor(reference, copy));
      root.dataset.referenceState = 'ready';
      content.hidden = false;
      status.textContent = copy.ready;
      document.dispatchEvent(new CustomEvent('kn:reference-updated'));
    } catch (error) {
      if (root.isConnected) {
        root.dataset.referenceState = 'unavailable';
        status.textContent = copy.unavailable;
        document.dispatchEvent(new CustomEvent('kn:reference-updated'));
      }
    } finally {
      window.clearTimeout(timeout);
      card.setAttribute('aria-busy', 'false');
    }
  }

  const initializeAll = () => document.querySelectorAll('[data-kn-contact]').forEach(initialize);
  initializeAll();
  document.addEventListener('shopify:section:load', initializeAll);
})();



